import type { IDisposable } from '../Utilities/IDisposable';
import { UnknownNameError } from '../DataFlow/DataFlowBuilder';
import type { IValue } from '../DataFlow/Graph';
import type { DataFlow, IDataFlowGetter, AncestryInfo, ValueConstructor } from '../DataFlow/DataFlow';
import type { Type } from '../DataFlow/Type';
import { requireEqualPrototypes } from '../DataFlow/Utils';

export class DataFlowSnapshot implements IDataFlowGetter, IDisposable {
  private readonly _parent: DataFlow;

  private readonly _snapshot = new Map<string, IValue | undefined>();

  public constructor(parent: DataFlow) {
    this._parent = parent;
    this._takeSnapshot();
  }

  private _takeSnapshot(): void {
    const allNames = this._parent.getAll();
    for (const name of allNames) {
      const value = this._parent.getIValue(name);
      this._snapshot.set(name, value);
    }
  }

  public dispose(): void {
    this._snapshot.clear();
  }

  // --------------- IDataFlowGetter Overrides

  public getIValue(name: string): IValue | undefined {
    if (!this.has(name)) {
      throw new UnknownNameError(name);
    }
    return this._snapshot.get(name);
  }

  public getValue<T extends IValue>(name: string, constructor: ValueConstructor<T>): T | undefined {
    const value = this.getIValue(name);
    if (!value) {
      return undefined;
    }
    requireEqualPrototypes([value.type], [constructor.Type]);
    return value as unknown as T;
  }

  public has(name: string): boolean {
    return this._snapshot.has(name);
  }

  // --------------- IDataFlowGetter Pass thru

  public getType(name: string): Type {
    if (!this.has(name)) {
      throw new UnknownNameError(name);
    }
    return this._parent.getType(name);
  }

  public getOutputsOf(names: Array<string>): Array<string> {
    return this._parent.getOutputsOf(names);
  }

  public getInputsFor(names: Array<string>): Array<string> {
    return this._parent.getInputsFor(names);
  }

  public getInputs(): Array<string> {
    return this._parent.getInputs();
  }

  public getOutputs(): Array<string> {
    return this._parent.getOutputs();
  }

  public getAll(): Array<string> {
    return this._parent.getAll();
  }

  public hasExtension(extensionDescriptor: string): boolean {
    return this._parent.hasExtension(extensionDescriptor);
  }

  public getAncestorsOf(name: string, calculatorNames: Set<string>): Map<number, AncestryInfo> {
    return this._parent.getAncestorsOf(name, calculatorNames);
  }
}
